import { useEffect, useState } from "react";
import { getFromLocalStorage, saveToLocalStorage } from './EncryptDecrypt.jsx';
import { domain } from './Domain.jsx';

// Shared refresh token control
let isRefreshing = false;
let refreshPromise = null;

const useFetch = (url) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refresh, setRefresh] = useState(false);

    // 🔄 Function to refresh access token (with lock)
    const fetchRefreshToken = async () => {
        if (isRefreshing) {
            return refreshPromise;
        }

        isRefreshing = true;
        refreshPromise = (async () => {
            const token = await getFromLocalStorage("authInfo");

            if (!token) throw new Error("No refresh token available");

            const response = await fetch(`${domain}/accounts/token/refresh`, {
                method: "POST",
                body: JSON.stringify({ refresh: token.refreshToken }),
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) throw new Error("Failed to refresh token");

            const data = await response.json();

            saveToLocalStorage("authInfo", {
                ...token,
                accessToken: data.access,
                refreshToken: data.refresh,
            });

            return data.access;
        })()
            .finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });

        return refreshPromise;
    };

    useEffect(() => {
        const abortCon = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                let headers = {};
                let token = await getFromLocalStorage("authInfo");

                if (token) {
                    headers.Authorization = `Token ${token.accessToken}`;
                }

                let apiResponse = await fetch(url, {
                    method: "GET",
                    headers,
                    signal: abortCon.signal,
                });

                // 🔁 Refresh token on 401
                if (apiResponse.status === 401) {
                    try {
                        console.log("🔄 Refreshing access token...");
                        const newAccessToken = await fetchRefreshToken();

                        // Retry the request with the new access token
                        headers.Authorization = `Token ${newAccessToken}`;
                        apiResponse = await fetch(url, {
                            method: "GET",
                            headers,
                            signal: abortCon.signal,
                        });
                    } catch (refreshError) {
                        console.error("🚨 Token refresh failed, logging out user...");
                        localStorage.removeItem("authInfo");
                        localStorage.removeItem("isLoggedIn");
                        const errorObj = {
                            message: "Unauthorized. Please log in again.",
                            status: 401,
                        };
                        setError(errorObj);
                        setLoading(false);
                        return;
                    }
                }

                // Try to parse the response (if applicable)
                const contentType = apiResponse.headers.get("content-type");
                let responseData = null;
                if (contentType && contentType.includes("application/json")) {
                    responseData = await apiResponse.json();
                }

                if (!apiResponse.ok) {
                    throw {
                        message: responseData?.error || `Request failed with status ${apiResponse.status}`,
                        status: apiResponse.status,
                        details: responseData,
                    };
                }

                setData(responseData);
                setLoading(false);
                setError(null);
            } catch (err) {
                if (err.name === "AbortError") {
                    console.log("fetch aborted");
                    return;
                }
                console.error("🚨 API Request Error:", err);
                const errorObj = {
                    message: err?.message || "Something went wrong",
                    status: err?.status || null,
                    details: err?.details || null,
                };
                setError(errorObj);
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            abortCon.abort();
        };
    }, [url, refresh]);

    return { data, loading, error, refresh, setRefresh, setData };
};

export default useFetch;